import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useControllableState } from '../lib/useControllableState.js';

export interface SplitPaneProps {
  /** `horizontal`: panes side by side (drag left-right). `vertical`: stacked (drag up-down). */
  direction?: 'horizontal' | 'vertical';
  /** Which pane the size applies to. Default `first`. */
  primary?: 'first' | 'second';
  /** Controlled primary size in px. */
  size?: number;
  defaultSize?: number;
  onResize?: (size: number) => void;
  min?: number;
  max?: number;
  /** Keyboard step in px. Default 16. */
  step?: number;
  /** Hide the primary pane entirely (collapsed rail, closed inspector). */
  collapsed?: boolean;
  /** Accessible name for the separator. */
  label?: string;
  children: [ReactNode, ReactNode];
  className?: string;
  /** Applied to the two pane wrappers. */
  paneClassName?: string;
}

/**
 * Two panes with a 1px --line divider whose 8px hit area drags with pointer events and moves with
 * arrow keys (role=separator, aria-valuenow). Sizes are clamped to [min, max] and reported via
 * onResize so the host can persist them.
 */
export function SplitPane({
  direction = 'horizontal',
  primary = 'first',
  size,
  defaultSize = 280,
  onResize,
  min = 120,
  max = Number.POSITIVE_INFINITY,
  step = 16,
  collapsed = false,
  label,
  children,
  className,
  paneClassName,
}: SplitPaneProps) {
  const [current, setCurrent] = useControllableState<number>({ value: size, defaultValue: defaultSize, onChange: onResize });
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ pointer: number; size: number } | null>(null);
  const horizontal = direction === 'horizontal';

  const clamp = useCallback(
    (value: number) => {
      const el = containerRef.current;
      const total = el ? (horizontal ? el.clientWidth : el.clientHeight) : Number.POSITIVE_INFINITY;
      const hardMax = Math.min(max, Math.max(min, total - 80));
      return Math.round(Math.min(hardMax, Math.max(min, value)));
    },
    [horizontal, max, min],
  );

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = { pointer: horizontal ? e.clientX : e.clientY, size: current };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const start = startRef.current;
    if (!start) return;
    const pos = horizontal ? e.clientX : e.clientY;
    const delta = pos - start.pointer;
    const signed = primary === 'first' ? delta : -delta;
    setCurrent(clamp(start.size + signed));
  };
  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    if (!startRef.current) return;
    startRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const grow = horizontal ? 'ArrowRight' : 'ArrowDown';
    const shrink = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const sign = primary === 'first' ? 1 : -1;
    let next: number | null = null;
    if (e.key === grow) next = current + step * sign;
    else if (e.key === shrink) next = current - step * sign;
    else if (e.key === 'Home') next = min;
    else if (e.key === 'End') next = Number.isFinite(max) ? max : current + 10_000;
    if (next === null) return;
    e.preventDefault();
    setCurrent(clamp(next));
  };

  useEffect(() => {
    if (!dragging) return;
    const prev = document.body.style.cursor;
    document.body.style.cursor = horizontal ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = prev;
      document.body.style.userSelect = '';
    };
  }, [dragging, horizontal]);

  const [first, second] = children;
  const primarySize = collapsed ? 0 : current;
  const primaryStyle = horizontal ? { width: primarySize, minWidth: primarySize } : { height: primarySize, minHeight: primarySize };
  const primaryClass = cn('relative min-h-0 min-w-0 shrink-0 overflow-hidden', paneClassName);
  const secondaryClass = cn('relative min-h-0 min-w-0 flex-1 overflow-hidden', paneClassName);
  const firstIsPrimary = primary === 'first';

  const handle = collapsed ? null : (
    <div
      role="separator"
      aria-label={label ?? (horizontal ? 'Resize panes horizontally' : 'Resize panes vertically')}
      aria-orientation={horizontal ? 'vertical' : 'horizontal'}
      aria-valuemin={min}
      aria-valuemax={Number.isFinite(max) ? max : undefined}
      aria-valuenow={current}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      className={cn(
        'group relative z-10 shrink-0 select-none touch-none outline-none',
        horizontal ? 'w-px cursor-col-resize' : 'h-px cursor-row-resize',
        'bg-line',
        'focus-visible:bg-brand',
        dragging ? 'bg-brand' : '',
      )}
    >
      <span aria-hidden className={cn('absolute', horizontal ? '-left-1 -right-1 inset-y-0' : '-top-1 -bottom-1 inset-x-0')} />
      <span
        aria-hidden
        className={cn(
          'absolute bg-brand opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none',
          horizontal ? 'inset-y-0 left-0 w-px' : 'inset-x-0 top-0 h-px',
          dragging ? 'opacity-100' : '',
        )}
      />
    </div>
  );

  return (
    <div ref={containerRef} className={cn('flex min-h-0 min-w-0', horizontal ? 'flex-row' : 'flex-col', className)} data-dragging={dragging || undefined}>
      {/* A collapsed pane is aria-hidden and inert so its controls leave the tab order (axe aria-hidden-focus). Added by desktop-renderer fix pass. */}
      <div className={firstIsPrimary ? primaryClass : secondaryClass} style={firstIsPrimary ? primaryStyle : undefined} aria-hidden={firstIsPrimary && collapsed ? true : undefined} inert={firstIsPrimary && collapsed ? true : undefined}>
        {first}
      </div>
      {handle}
      <div className={firstIsPrimary ? secondaryClass : primaryClass} style={firstIsPrimary ? undefined : primaryStyle} aria-hidden={!firstIsPrimary && collapsed ? true : undefined} inert={!firstIsPrimary && collapsed ? true : undefined}>
        {second}
      </div>
    </div>
  );
}
